import { ref } from "vue";

type AccentKey = "¨" | "^" | "~" | "´" | "`";

const accentLetterMap: Record<AccentKey, Record<string, string>> = {
  "¨": {
    a: "ä",
    e: "ë",
    i: "ï",
    o: "ö",
    u: "ü",
    y: "ÿ"
  },
  "^": {
    a: "â",
    e: "ê",
    i: "î",
    o: "ô",
    u: "û",
    y: "ŷ"
  },
  "~": {
    a: "ã",
    n: "ñ",
    o: "õ"
  },
  "´": {
    a: "á",
    e: "é",
    i: "í",
    o: "ó",
    u: "ú",
    y: "ý"
  },
  "`": {
    a: "à",
    e: "è",
    i: "ì",
    o: "ò",
    u: "ù",
    y: "ỳ"
  }
};

export function useDragAndDropKeyboard() {
  const accentBuffer = ref<AccentKey | null>(null);

  function onKey(event: KeyboardEvent): string | null {
    if (event.ctrlKey || event.metaKey) {
      return null;
    }

    if (isAccentKey(event)) {
      accentBuffer.value = getAccentKeyValue(event);
      return null;
    }

    if (keyShouldReset(event)) {
      resetAccentBuffer();
    }

    if (accentBuffer.value) {
      const value = accentLetterMap[accentBuffer.value][event.key.toLowerCase()];
      resetAccentBuffer();
      return value;
    }

    return event.key;
  }

  function resetAccentBuffer() {
    accentBuffer.value = null;
  }

  /**
   * Check if the key is an IME key. Note that this only supports Danish keyboards.
   */
  function isAccentKey(event: KeyboardEvent): boolean {
    return ["BracketRight", "Equal"].includes(event.code);
  }

  /**
   * Get the value of the IME key. Note that this only supports Danish keyboards.
   */
  function getAccentKeyValue(event: KeyboardEvent): AccentKey | null {
    if (event.code === "BracketRight") {
      switch (true) {
        case event.shiftKey:
          return "^";
        case event.altKey:
          return "~";
        default:
          return "¨";
      }
    }

    if (event.code === "Equal") {
      switch (true) {
        case event.shiftKey:
          return "`";
        default:
          return "´";
      }
    }

    return null;
  }

  function keyShouldReset(event: KeyboardEvent): boolean {
    return ["Backspace", "Space", "Enter"].includes(event.code);
  }

  return { onKey, resetAccentBuffer };
}
